import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {{requestUrl: string, morphElementSelector?: string, updateUrl: boolean} | undefined} */
  #retryRequest;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !isOnFeaturedProductSection &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const requestUrl = this.buildRequestUrl(selectedOption);
    const currentUrl = new URL(this.dataset.productUrl ?? window.location.href, window.location.origin).pathname;
    const newUrl = new URL(requestUrl, window.location.origin).pathname;
    const loadsNewProduct = isOnProductPage && newUrl !== currentUrl;
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(requestUrl, morphElementSelector, isOnProductPage);
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const isSameProductPage = !source && this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card, quick-add-dialog') && this.dataset.sectionId &&
      new URL(productUrl, window.location.origin).pathname ===
        new URL(this.dataset.productUrl, window.location.origin).pathname;
    if (isSameProductPage) params.push(`section_id=${encodeURIComponent(this.dataset.sectionId)}`);

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [updateUrl] - Whether to synchronize the product-page URL with the resolved variant.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, updateUrl = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.#retryRequest = { requestUrl, morphElementSelector, updateUrl };
    this.#clearVariantError();

    return this.#fetchVariantResponse(requestUrl, abortController.signal)
      .then((response) => {
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent?.trim()) throw new Error('Variant response is missing its selection');
        const variant = JSON.parse(textContent);
        const variantId = variant?.id;
        const validVariantId = (typeof variantId === 'number' ||
          (typeof variantId === 'string' && /^[1-9]\d*$/.test(variantId))) &&
          Number.isSafeInteger(Number(variantId)) && Number(variantId) > 0;
        // A literal null confirms a genuinely unavailable option combination.
        if (variant !== null && (typeof variant !== 'object' || Array.isArray(variant) || !validVariantId)) {
          throw new Error('Variant response is invalid');
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        this.#pendingRequestUrl = undefined;
        this.#retryRequest = undefined;
        this.#clearVariantError();

        // Option datasets can still describe the previous combination during
        // rapid changes. Only the latest rendered response confirms its variant.
        if (updateUrl && !abortController.signal.aborted) {
          const url = new URL(window.location.href);
          if (variant?.id) {
            url.searchParams.set('variant', variant.id);
          } else {
            url.searchParams.delete('variant');
          }
          if (morphElementSelector === 'main') {
            url.pathname = new URL(requestUrl, window.location.origin).pathname;
          }
          if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted || this.#abortController !== abortController || error.name === 'AbortError') return;
        console.error(error);
        this.#showVariantError();
        this.dispatchEvent(new CustomEvent('variant:error', { bubbles: true, detail: { productId: this.dataset.productId } }));
      });
  }

  /**
   * Retry only temporary server failures, retaining the exact localized URL.
   * A newer selection cancels both the request and any pending retry delay.
   * @param {string} requestUrl
   * @param {AbortSignal} signal
   * @returns {Promise<Response>}
   */
  async #fetchVariantResponse(requestUrl, signal) {
    for (let attempt = 0; ; attempt++) {
      if (signal.aborted || (attempt > 0 && !this.isConnected)) throw new DOMException('Aborted', 'AbortError');
      const response = await fetch(requestUrl, { signal });
      const requestedUrl = new URL(requestUrl, window.location.origin);
      const ownSection = requestedUrl.searchParams.get('section_id') === this.dataset.sectionId &&
        this.dataset.templateProductMatch === 'true' &&
        requestedUrl.pathname === new URL(this.dataset.productUrl, window.location.origin).pathname;
      if (response.ok && ownSection) {
        const section = new DOMParser().parseFromString(await response.clone().text(), 'text/html');
        const picker = section.querySelector('variant-picker');
        if (picker?.dataset.renderedLocale !== document.documentElement.lang ||
            picker?.dataset.productId !== this.dataset.productId ||
            picker?.dataset.sectionId !== this.dataset.sectionId) {
          throw new Error('Variant section returned a different language, product or section');
        }
        const selected = new Set(Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked'))
          .map((option) => option.getAttribute('data-option-value-id')));
        const requested = requestedUrl.searchParams.get('option_values')?.split(',') ?? [];
        if (requested.some((id) => !selected.has(id))) throw new Error('Variant section returned different options');
      }
      if (attempt > 0 && response.ok && !requestedUrl.searchParams.has('section_id')) {
        const recovered = new DOMParser().parseFromString(await response.clone().text(), 'text/html');
        if (recovered.documentElement.lang !== document.documentElement.lang) {
          throw new Error('Variant recovery returned a different language');
        }
        const picker = recovered.querySelector('variant-picker');
        const recoveredOptions = new Set(
          Array.from(picker?.querySelectorAll('select option[selected], fieldset input:checked') ?? [])
            .map((option) => option.getAttribute('data-option-value-id'))
        );
        const requestedOptions = requestedUrl.searchParams.get('option_values')?.split(',') ?? [];
        if (requestedOptions.some((id) => !recoveredOptions.has(id))) {
          throw new Error('Variant recovery returned a different option selection');
        }
      }
      if (attempt === 2 || ![500, 502, 503, 504].includes(response.status)) return response;

      await response.body?.cancel().catch(() => {});
      await new Promise((resolve, reject) => {
        const cancel = () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        };
        const timer = setTimeout(() => {
          signal.removeEventListener('abort', cancel);
          resolve();
        }, attempt === 0 ? 250 : 750);
        signal.addEventListener('abort', cancel, { once: true });
        if (signal.aborted) {
          signal.removeEventListener('abort', cancel);
          cancel();
        }
      });
    }
  }

  #clearVariantError() {
    const error = this.querySelector('[data-variant-error]');
    if (error?.dataset.variantError !== undefined) {
      error.hidden = true;
      const alert = error.querySelector('[role="alert"]');
      if (alert) alert.textContent = '';
    }
  }

  #showVariantError() {
    let error = this.querySelector('[data-variant-error]');
    if (error?.dataset.variantError === undefined) {
      error = document.createElement('div');
      error.dataset.variantError = '';
      const alert = document.createElement('p');
      alert.setAttribute('role', 'alert');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button-secondary';
      retry.textContent = Theme.translations.retry;
      retry.addEventListener('click', () => {
        const request = this.#retryRequest;
        if (request) this.fetchUpdatedSection(request.requestUrl, request.morphElementSelector, request.updateUrl);
      });
      error.append(alert, retry);
      this.appendChild(error);
    }
    error.hidden = false;
    const alert = error.querySelector('[role="alert"]');
    if (alert) alert.textContent = Theme.translations.variant_error;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
